import { useCallback, useMemo, useState, type ReactNode } from "react";
import { PageShortcutsContext, type PageShortcutSection } from "./usePageShortcuts";

export const PageShortcutsProvider = ({ children }: { children: ReactNode }) => {
  const [registry, setRegistry] = useState<Record<string, PageShortcutSection[]>>({});

  const register = useCallback((id: string, sections: PageShortcutSection[]) => {
    setRegistry((prev) => {
      // No-op when the sections for this id are unchanged, so a page that
      // re-registers on every render can't drive an infinite update loop.
      if (id in prev && JSON.stringify(prev[id]) === JSON.stringify(sections)) return prev;
      return { ...prev, [id]: sections };
    });
  }, []);


  const unregister = useCallback((id: string) => {
    setRegistry((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const sections = useMemo(() => Object.values(registry).flat(), [registry]);

  const value = useMemo(
    () => ({ sections, register, unregister }),
    [sections, register, unregister]
  );

  return <PageShortcutsContext.Provider value={value}>{children}</PageShortcutsContext.Provider>;
};
